import { usePdfiumEngine } from "@embedpdf/engines/react";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-hot-toast";
import { DraftDocxViewer, type DraftDocxEditorHandle } from "@/components/drafts/DraftDocxViewer";
import Spinner from "@/components/shared/Spinner";
import { Button } from "@/components/ui/NewButton";
import { readDocxArrayBufferFromBlob } from "@/lib/docxBuffer";
import { pdfiumWasmUrl } from "@/lib/pdfium";
import type { FontOption } from "./bundleTypes";

// ── Font registration ──

let fontsRegistered = false;
export async function registerFontsWithEditor(fontOptions: FontOption[]) {
  if (fontsRegistered || fontOptions.length === 0) return;

  for (const font of fontOptions) {
    const name = font.label;
    try {
      const variants: { url: string; weight: number }[] = [
        { url: font.files.regular, weight: 400 },
        { url: font.files.bold, weight: 700 },
        { url: font.files.italic, weight: 400 },
        { url: font.files.bold_italic, weight: 700 },
      ];
      for (const v of variants) {
        const res = await fetch(v.url);
        if (res.ok) {
          const buffer = await res.arrayBuffer();
          const face = new FontFace(name, buffer, {
            style: v.url === font.files.italic || v.url === font.files.bold_italic ? 'italic' : 'normal',
            weight: String(v.weight),
          });
          await face.load();
          document.fonts.add(face);
        }
      }
    } catch {
      // Font load failure is non-critical
    }
  }

  fontsRegistered = true;
}

// ── Constants ──

const INLINE_SCALE = 0.5;
export const INLINE_PREVIEW_WIDTH = 816 * INLINE_SCALE;
export const INLINE_PREVIEW_HEIGHT = 1056 * INLINE_SCALE;
const TITLE_PAGE_EDITOR_PORTAL_Z_INDEX = 99999;
const TITLE_PAGE_SUPERDOC_FLOATING_Z_INDEX = 1000000;
const TITLE_PAGE_SUPERDOC_BACKDROP_Z_INDEX = TITLE_PAGE_SUPERDOC_FLOATING_Z_INDEX - 1;

// ── Main component ──

type InlineTitlePageProps = {
  buffer: ArrayBuffer | null;
  format: 'pdf' | 'docx';
  loading: boolean;
  fontOptions: FontOption[];
};

type PopupTitlePageProps = {
  buffer: ArrayBuffer | null;
  format: 'pdf' | 'docx';
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful save (DOCX only) — receives the edited buffer */
  onSaved: (buffer: ArrayBuffer) => void;
  fontOptions: FontOption[];
};

export const PopupTitlePage = ({ open, onOpenChange, onSaved, buffer, format, fontOptions }: PopupTitlePageProps) => {
  if (!open) return null;
  if (format === 'docx') {
    return (
      <DocxPopup
        open={open}
        onOpenChange={onOpenChange}
        onSaved={onSaved}
        buffer={buffer}
        fontOptions={fontOptions}
      />
    );
  }
  return (
    <PdfPopup
      open={open}
      onOpenChange={onOpenChange}
      buffer={buffer}
    />
  );
};

const INLINE_DOCX_STYLES = `
  /* Full-size SuperDoc page, CSS-scaled into the fixed thumbnail box.
     SuperDoc v2 fit-width/manual zoom does not reliably fit this surface. */
  .title-page-preview-scaler {
    width: 200%;
    height: 200%;
    transform: scale(${INLINE_SCALE});
    transform-origin: top left;
    pointer-events: none;
    background: white !important;
  }
  .title-page-preview-scaler .superdoc__sub-document {
    overflow: hidden !important;
  }
  .title-page-preview-scaler .draft-docx-editor {
    background: white !important;
    --sd-layout-page-shadow: none;
  }
  .title-page-preview::after {
    content: '';
    position: absolute;
    top: -1px;
    right: -1px;
    bottom: -1px;
    width: 8px;
    background: white;
    z-index: 9999;
    border-right: 1px solid #e5e7eb;
    border-top-right-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
  }
`;

export const InlineTitlePage = ({ buffer, format, loading, fontOptions }: InlineTitlePageProps) => {
  useEffect(() => {
    if (fontOptions?.length) registerFontsWithEditor(fontOptions);
  }, [fontOptions]);

  if (loading) {
    return <div className="flex items-center justify-center h-full"><Spinner size="lg" /></div>;
  }
  if (!buffer) {
    return <div className="flex items-center justify-center h-full text-sm text-gray-400">No preview available</div>;
  }
  if (format === 'pdf') {
    return <PdfPageRenderer buffer={buffer} scale={INLINE_SCALE} />;
  }
  return (
    <>
      <style>{INLINE_DOCX_STYLES}</style>
      <div className="title-page-preview-scaler">
        <DraftDocxViewer
          buffer={buffer}
          centerScroll={false}
          documentId="title-page-inline"
          emptyClassName="flex h-full items-center justify-center text-sm text-gray-400"
          error={null}
          frameClassName="draft-docx-editor title-page-preview h-full min-h-0 overflow-hidden bg-white"
          initialZoom={1}
          isEditing={false}
          isLoading={false}
          renderInPlace
          title="Title Page Preview"
          versionKey={buffer.byteLength}
        />
      </div>
    </>
  );
};

// Portal to document.body instead of using Radix Dialog because an ancestor
// `animate-page-in` div has `transform: matrix(1,0,0,1,0,0)` which creates a
// containing block that breaks `position: fixed` dropdown positioning in the
// docx-js-editor library.
const PopupShell = ({ onClose, children, className }: {
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
}) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-99998">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      {/* Centered fixed-width panel: left-1/2 + -translate-x-1/2 centers horizontally,
          top/bottom inset gives vertical padding, max-w-[95vw] prevents overflow on small screens */}
      <div
        className={`absolute top-[2.5vh] bottom-[2.5vh] left-1/2 -translate-x-1/2 w-[72rem] max-w-[95vw] flex flex-col bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden ${className ?? ''}`}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
};

const PdfPopup = ({ open, onOpenChange, buffer }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  buffer: ArrayBuffer | null;
}) => {
  if (!open) return null;

  return (
    <PopupShell onClose={() => onOpenChange(false)}>
      <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
        <h2 className="text-lg font-semibold">Title Page Preview</h2>
        <button type="button" onClick={() => onOpenChange(false)} className="text-sm text-gray-500 hover:text-gray-700">Close</button>
      </div>
      <div className="flex-1 min-h-0 overflow-auto flex justify-center py-6">
        {buffer ? <PdfPageRenderer buffer={buffer} /> : <div className="text-sm text-gray-400">No preview available</div>}
      </div>
    </PopupShell>
  );
};

const EDITOR_DOCX_STYLES = `
  :root {
    --sd-popover-z-index: ${TITLE_PAGE_SUPERDOC_FLOATING_Z_INDEX};
    --sd-external-link-popover-z-index: ${TITLE_PAGE_SUPERDOC_FLOATING_Z_INDEX};
  }
  [data-radix-select-content],
  [data-radix-popper-content-wrapper],
  [role="listbox"] {
    z-index: ${TITLE_PAGE_SUPERDOC_FLOATING_Z_INDEX} !important;
  }
  [data-editor-ui-surface],
  .sd-surface-host,
  .sd-surface-external-mount {
    z-index: ${TITLE_PAGE_SUPERDOC_FLOATING_Z_INDEX} !important;
  }
  .sd-surface-dialog-backdrop {
    z-index: ${TITLE_PAGE_SUPERDOC_BACKDROP_Z_INDEX} !important;
  }
  .sd-surface-floating,
  .sd-surface-dialog,
  .sd-editor-popover,
  .sd-editor-toolbar-dropdown,
  .sd-editor-toolbar-tooltip,
  .sd-internal-dropdown,
  .sd-toolbar-dropdown-menu,
  .superdoc-toolbar-overflow,
  .toolbar-dropdown-menu,
  .comments-dropdown__menu,
  .context-menu,
  .generic-popover,
  .superdoc__compact-comment-popover {
    z-index: ${TITLE_PAGE_SUPERDOC_FLOATING_Z_INDEX} !important;
  }
  .title-page-editor-panel [aria-label="Menu bar"] > :last-child {
    display: none !important;
  }
  .title-page-editor-panel button[title*="Editing"],
  .title-page-editor-panel button[aria-label="Toggle comments sidebar"],
  .title-page-editor-panel button[aria-label="Clear formatting"],
  .title-page-editor-panel button[aria-label="Clear formatting"] + [role="separator"],
  .title-page-editor-panel [role="separator"]:has(+ button[aria-label="Toggle comments sidebar"]),
  .title-page-editor-panel button[aria-label="Toggle comments sidebar"] + [role="separator"] {
    display: none !important;
  }
`;

const DocxPopup = ({ open, onOpenChange, onSaved, buffer, fontOptions }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (buffer: ArrayBuffer) => void;
  buffer: ArrayBuffer | null;
  fontOptions: FontOption[];
}) => {
  const editorRef = useRef<DraftDocxEditorHandle | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (fontOptions?.length) registerFontsWithEditor(fontOptions);
  }, [fontOptions]);

  const handleSave = useCallback(async () => {
    if (!editorRef.current) { toast.error("Editor not ready"); return; }
    setSaving(true);
    try {
      const superdoc = editorRef.current.getSuperDoc();
      if (!superdoc) { toast.error("Editor not ready"); return; }
      // SuperDoc v2 worker-backed save does not support commentsType:'clean'
      // (maps to commentExportMode:'strip' and fails closed). Title-page editing
      // already disables comments, so preserve/external is equivalent here.
      const docxBlob = await superdoc.export({
        commentsType: 'external',
        exportType: ['docx'],
        fieldsHighlightColor: null,
        isFinalDoc: true,
        triggerDownload: false,
      });
      const docxBuffer = await readDocxArrayBufferFromBlob(docxBlob);
      if (!docxBuffer) { toast.error("Failed to export document"); return; }
      onSaved?.(docxBuffer);
      onOpenChange(false);
    } catch {
      toast.error("Error saving title page");
    } finally {
      setSaving(false);
    }
  }, [onOpenChange, onSaved]);

  if (!open) return null;

  return (
    <>
      <style>{EDITOR_DOCX_STYLES}</style>
      <PopupShell onClose={() => onOpenChange(false)} className="title-page-editor-panel">
        <div className="flex-1 min-h-0">
          {buffer ? (
            <DraftDocxViewer
              buffer={buffer}
              documentId="title-page-popup"
              editorRef={editorRef}
              emptyClassName="flex h-full items-center justify-center text-sm text-gray-400"
              error={null}
              frameClassName="draft-docx-editor title-page-editor-panel h-full min-h-0 overflow-hidden bg-white"
              isEditing
              isLoading={false}
              placeholderClassName="h-full"
              portalZIndex={TITLE_PAGE_EDITOR_PORTAL_Z_INDEX}
              stripCrimsonNodeAnchors
              title="Edit Title Page"
              versionKey={buffer.byteLength}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-sm text-gray-400">No document to edit</div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t shrink-0">
          <Button text="Cancel" variant="outline" onClick={() => onOpenChange(false)} />
          <Button text="Update" loading={saving} onClick={handleSave} disabled={!buffer || saving} />
        </div>
      </PopupShell>
    </>
  );
};

// ── PDF page renderer (internal) ──

const PdfPageRenderer = ({ buffer, scale }: { buffer: ArrayBuffer; scale?: number }) => {
  const { engine, isLoading } = usePdfiumEngine({ wasmUrl: pdfiumWasmUrl });
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const prevSrc = useRef<string | null>(null);

  useEffect(() => {
    if (!engine) return;
    let cancelled = false;

    (async () => {
      try {
        const doc = await engine.openDocumentBuffer({ id: `title-page-${Date.now()}`, content: buffer.slice(0) }).toPromise();
        if (cancelled || !doc.pages.length) return;
        const blob = await engine.renderPage(doc, doc.pages[0], { scaleFactor: 2 }).toPromise();
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        setImageSrc(url);
        if (prevSrc.current) URL.revokeObjectURL(prevSrc.current);
        prevSrc.current = url;
        await engine.closeDocument(doc).toPromise();
      } catch {
        if (!cancelled) setError(true);
      }
    })();

    return () => { cancelled = true; };
  }, [engine, buffer]);

  useEffect(() => { return () => { if (prevSrc.current) URL.revokeObjectURL(prevSrc.current); }; }, []);

  if (error) return <div className="flex items-center justify-center h-full text-sm text-gray-400">Failed to load PDF preview</div>;
  if (isLoading || !imageSrc) return <div className="flex items-center justify-center h-full"><Spinner size="lg" /></div>;

  return (
    <div style={scale && scale !== 1 ? { transform: `scale(${scale})`, transformOrigin: "top left", width: `${100 / scale}%`, height: `${100 / scale}%` } : undefined}>
      <img src={imageSrc} alt="Title page preview" style={{ display: "block", width: "100%" }} />
    </div>
  );
};
